import { type FC, type KeyboardEvent } from "react";
import { ClickableIcon } from "../../primitives";
import {
	useFinancialGridDispatch,
	useFinancialGridState,
} from "./state/FinancialGridStateProvider";

type RenameLabelInputProps = {
	rowId: string;
	onCommit: (newLabel: string) => void;
};

export const RenameLabelInput: FC<RenameLabelInputProps> = ({ rowId, onCommit }) => {
	const state = useFinancialGridState();
	const dispatch = useFinancialGridDispatch();

	if (state.edit.kind !== "editing_label" || state.edit.rowId !== rowId) return null;

	const { labelDraft } = state.edit;

	const commit = () => onCommit(labelDraft);
	const cancel = () => dispatch({ type: "cancel_rename_row" });

	const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			commit();
		} else if (e.key === "Escape") {
			e.preventDefault();
			cancel();
		}
	};

	return (
		<span className="ml-2 flex-1 min-w-0 flex items-center gap-2">
			<input
				type="text"
				value={labelDraft}
				onChange={(e) => dispatch({ type: "change_rename_label", label: e.target.value })}
				onKeyDown={handleKey}
				autoFocus
				data-testid={`rename-line-item-input-${rowId}`}
				className="flex-1 min-w-0 bg-surface-base border border-bc-strong rounded-sm px-2 py-0.5 text-sm text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
			/>
			<ClickableIcon
				onClick={commit}
				data-testid={`rename-line-item-confirm-${rowId}`}
				aria-label="Confirm rename"
				className="w-5 h-5 rounded-sm hover:bg-surface-light shrink-0"
				type="check"
				size="mini"
				color="confirm"
			/>
			<ClickableIcon
				onClick={cancel}
				data-testid={`rename-line-item-cancel-${rowId}`}
				aria-label="Cancel rename"
				className="w-5 h-5 rounded-sm hover:bg-surface-light shrink-0"
				type="x-mark"
				size="mini"
				color="danger"
			/>
		</span>
	);
};
